import type { ProviderEligibilityBlockingReason } from "@tinkr/shared/provider-governance";

import type {
  ProviderApplicationStatusResponse,
  ProviderFulfillmentStanding
} from "./tinkr-api";

/**
 * One derived provider display state for the whole console (ADR-W-E1).
 *
 * Before this module the sidebar chip read only `acceptingJobs`, the Home
 * variant read `canReceiveOrders`, and the lockout read
 * `submittedForReviewAt && operationalStatus !== "active"`. Those three
 * predicates disagreed: a suspended provider was told they were "in review",
 * a paused live provider got the day-one checklist, and an admin decision of
 * "more information required" or "rejected" locked the provider out with
 * "we're reviewing your account".
 *
 * This is a pure derivation over facts the API already returns:
 *   - `/v1/provider/application` (gate, provider summary with
 *     businessReview, eligibility and submittedForReviewAt),
 *   - the shop's own `acceptingJobs` switch (shop-status/profile; fresher
 *     than the application summary after a toggle in Operations),
 *   - `/v1/provider/fulfillment-standing` (strike pause, which is NOT part of
 *     the eligibility decision: matching excludes `fulfillment_paused_at`
 *     providers separately).
 *
 * "Open for jobs" is a positive claim, so it is made only once the facts that
 * could contradict it have been read: the access summary's eligibility (a
 * hydrated localStorage hint carries none) and the strike standing. While
 * they are loading the state is `checking_offers`; if they could not be read
 * it is `offers_unconfirmed`. Both are still operational (Home keeps jobs
 * and earnings) but neither claims offers are flowing.
 *
 * It never grants access: every panel stays server-gated. It only decides
 * which explanation and which surface the provider sees.
 */
export type ProviderDisplayStateKind =
  | "loading"
  | "status_unavailable"
  | "setting_up"
  | "in_review"
  | "more_information_required"
  | "rejected"
  | "open_for_jobs"
  /** Active, nothing known to block offers yet, but the reads that could
   * contradict that are still loading. */
  | "checking_offers"
  /** Active, but those reads failed: offers may or may not be flowing. */
  | "offers_unconfirmed"
  | "paused_by_you"
  | "paused_by_tinkr"
  | "not_receiving_offers"
  | "suspended"
  | "closed";

/** Why a live (approved, active) provider is not receiving new offers. */
export type ProviderOfferBlocker =
  | { kind: "paused_by_tinkr"; standing: ProviderFulfillmentStanding }
  | { kind: "paused_by_you" }
  | { kind: "country_not_enabled" }
  | { kind: "agreement_required" }
  | { kind: "profile_incomplete" }
  | { kind: "review_required" };

/** Which full-page surface replaces the section content, if any. */
export type ProviderLockout =
  | "none"
  | "loading"
  | "unavailable"
  | "review"
  | "suspended"
  | "closed";

export type ProviderDisplayState = {
  kind: ProviderDisplayStateKind;
  /** An approved provider with an active record: Home shows the live
   * variant (jobs, earnings, offers) even while new offers are blocked. */
  operational: boolean;
  lockout: ProviderLockout;
  /** Ordered most-severe first; empty when the provider is open for jobs. */
  blockers: ProviderOfferBlocker[];
  /** Admin review decision to explain above the setup flow. */
  reviewDecision: "more_information_required" | "rejected" | null;
  submittedAt: string | null;
};

export type ProviderDisplayStateInput = {
  access: ProviderApplicationStatusResponse | null;
  /** True when the access read failed and nothing was hydrated. */
  accessFailed?: boolean;
  /** The shop's own intake switch when a fresher read is available. */
  acceptingJobs?: boolean | null;
  standing?: ProviderFulfillmentStanding | null;
  /**
   * Whether the reads that can contradict "open for jobs" have settled
   * (eligibility from a real access read, and the strike standing). Omitted
   * means settled, for callers that derive from a complete snapshot.
   */
  offerFacts?: "ready" | "loading" | "failed";
};

const BLOCKER_FOR_REASON: Partial<Record<ProviderEligibilityBlockingReason, ProviderOfferBlocker["kind"]>> = {
  COUNTRY_NOT_ENABLED: "country_not_enabled",
  PROVIDER_AGREEMENT_NOT_ACCEPTED: "agreement_required",
  PROFILE_INCOMPLETE: "profile_incomplete",
  IDENTITY_NOT_VERIFIED: "review_required",
  BUSINESS_NOT_APPROVED: "review_required"
};

function state(
  kind: ProviderDisplayStateKind,
  lockout: ProviderLockout,
  extra: Partial<ProviderDisplayState> = {}
): ProviderDisplayState {
  return {
    kind,
    lockout,
    operational: false,
    blockers: [],
    reviewDecision: null,
    submittedAt: null,
    ...extra
  };
}

export function deriveProviderDisplayState(input: ProviderDisplayStateInput): ProviderDisplayState {
  const { access } = input;
  if (!access) {
    return input.accessFailed ? state("status_unavailable", "unavailable") : state("loading", "loading");
  }
  const provider = access.provider;
  const submittedAt = provider?.submittedForReviewAt ?? access.application?.createdAt ?? null;

  if (!provider) {
    if (access.gate.status === "rejected") return state("rejected", "none", { reviewDecision: "rejected" });
    if (access.gate.status === "pending_admin_approval" || access.gate.status === "awaiting_network_launch") {
      return state("in_review", "review", { submittedAt });
    }
    return state("setting_up", "none");
  }

  if (provider.operationalStatus === "deleted") return state("closed", "closed");
  if (provider.operationalStatus === "suspended") return state("suspended", "suspended");

  if (provider.operationalStatus === "active") {
    const blockers = offerBlockers(input);
    const unsettled = input.offerFacts === "loading" || input.offerFacts === "failed";
    const kind: ProviderDisplayStateKind =
      blockers.length === 0
        ? !unsettled
          ? "open_for_jobs"
          : input.offerFacts === "loading"
            ? "checking_offers"
            : "offers_unconfirmed"
        : blockers[0]!.kind === "paused_by_tinkr"
          ? "paused_by_tinkr"
          : blockers[0]!.kind === "paused_by_you"
            ? "paused_by_you"
            : "not_receiving_offers";
    return state(kind, "none", { operational: true, blockers, submittedAt: provider.submittedForReviewAt });
  }

  // Inactive provider record: the admin decision wins over the submit stamp,
  // which the business-review transition never clears.
  const review = provider.businessReview?.status;
  if (review === "more_information_required" || review === "rejected") {
    return state(review, "none", { reviewDecision: review, submittedAt: provider.submittedForReviewAt });
  }
  if (access.gate.status === "pending_admin_approval" || provider.submittedForReviewAt) {
    return state("in_review", "review", { submittedAt });
  }
  return state("setting_up", "none");
}

function offerBlockers(input: ProviderDisplayStateInput): ProviderOfferBlocker[] {
  const blockers: ProviderOfferBlocker[] = [];
  const standing = input.standing ?? null;
  if (standing?.fulfillmentPausedAt) blockers.push({ kind: "paused_by_tinkr", standing });
  const reasons = input.access?.provider?.eligibility?.orderBlockingReasons ?? [];
  // A fresh intake read beats the summary's (possibly stale) reason list.
  const paused =
    typeof input.acceptingJobs === "boolean"
      ? !input.acceptingJobs
      : reasons.includes("PROVIDER_NOT_ACCEPTING_ORDERS");
  if (paused) blockers.push({ kind: "paused_by_you" });
  const seen = new Set<ProviderOfferBlocker["kind"]>();
  for (const reason of reasons) {
    const kind = BLOCKER_FOR_REASON[reason];
    if (!kind || seen.has(kind)) continue;
    seen.add(kind);
    blockers.push({ kind } as ProviderOfferBlocker);
  }
  return blockers;
}

/** Sidebar chip tone: teal dot for live, orange diamond for setup/paused,
 * warning triangle for states that need the provider's attention. */
export type ProviderChipTone = "live" | "setup" | "warn";

export function providerChipTone(kind: ProviderDisplayStateKind): ProviderChipTone | null {
  switch (kind) {
    case "loading":
      return null;
    case "open_for_jobs":
      return "live";
    case "checking_offers":
    case "setting_up":
    case "in_review":
    case "paused_by_you":
      return "setup";
    default:
      return "warn";
  }
}

/** Translation key for the chip and the Home status line. */
export const PROVIDER_DISPLAY_STATE_LABEL_KEYS = {
  status_unavailable: "providerState.label.statusUnavailable",
  setting_up: "providerState.label.settingUp",
  in_review: "providerState.label.inReview",
  more_information_required: "providerState.label.moreInformationRequired",
  rejected: "providerState.label.rejected",
  open_for_jobs: "providerState.label.openForJobs",
  checking_offers: "providerState.label.checkingOffers",
  // Same wording as an unreadable account status: the chip says the provider's
  // state could not be confirmed, not that offers are on or off.
  offers_unconfirmed: "providerState.label.statusUnavailable",
  paused_by_you: "providerState.label.pausedByYou",
  paused_by_tinkr: "providerState.label.pausedByTinkr",
  not_receiving_offers: "providerState.label.notReceivingOffers",
  suspended: "providerState.label.suspended",
  closed: "providerState.label.closed"
} as const satisfies Record<Exclude<ProviderDisplayStateKind, "loading">, string>;

/** The console is "approved" once the provider can do daily work: Home,
 * the work queue, shop setup, payouts. Before that the navigation is
 * Home / Setup / Account (PUX-15). */
export function providerNavigationStage(state: ProviderDisplayState): "setup" | "approved" | "minimal" {
  if (state.operational) return "approved";
  switch (state.kind) {
    case "setting_up":
    case "in_review":
    case "more_information_required":
    case "rejected":
      return "setup";
    default:
      // Loading, status unavailable, suspended, closed: Home (which explains
      // the state) and Account only. Suspended access is not widened here.
      return "minimal";
  }
}
